import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import defaults from './default-style.json'

export type StyleSettings = Omit<typeof defaults, 'mark' | 'layout'> & { layout?: typeof defaults.layout } & { mark: typeof defaults.mark & { imageDataUrl?: string | null } }
export type PaletteKey = keyof StyleSettings['light']
export interface StyleView { id: string; name: string; scope: 'global' | 'tenant'; revision: string; settings: StyleSettings }
export const defaultStyle: StyleSettings = defaults
export const defaultPresetId = '32960000-0000-4000-8000-000000000001'
export const styleFonts: Record<string, string> = {
    system: 'ui-sans-serif, system-ui, sans-serif',
    sans: 'Arial, Helvetica, sans-serif',
    serif: 'Georgia, Cambria, serif',
}

export function styleVariables(settings: StyleSettings): CSSProperties {
    const vars: Record<string, string> = {
        '--style-font': styleFonts[settings.fontFamily] ?? styleFonts.system!,
        '--style-radius': settings.radius + 'px',
        '--style-mark-bg': settings.mark.background,
        '--style-mark-fg': settings.mark.foreground,
    }
    for (const [key, value] of Object.entries(settings.layout ?? defaults.layout)) vars[`--layout-${key}`] = `${value}px`
    for (const mode of ['light', 'dark'] as const) {
        for (const [key, value] of Object.entries(settings[mode])) vars[`--style-${mode}-${key}`] = value
    }
    return vars as CSSProperties
}

const StyleContext = createContext<StyleSettings>(defaultStyle)
export function BrandMark() {
    const settings = useContext(StyleContext)
    const [failedImage, setFailedImage] = useState<string | null>(null)
    const image = settings.mark.imageDataUrl
    return <span className="marque-brand-mark" aria-label="Application mark">
        {image && image !== failedImage ? <img src={image} alt="Company logo" onError={() => setFailedImage(image)} /> : settings.mark.text}
    </span>
}

export function StyleScope({ settings, dark = false, children }: { settings: StyleSettings; dark?: boolean; children: ReactNode }) {
    return <StyleContext.Provider value={settings}>
        <div data-marque-style={dark ? 'dark' : 'light'} className={dark ? 'dark' : ''} style={styleVariables(settings)}>{children}</div>
    </StyleContext.Provider>
}

export async function styleRequest<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
    const response = await fetch(path, {
        method, credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (!response.ok) {
        let message = `Unable to save or load styles (${response.status}).`
        try {
            const error = await response.json() as { error?: string; detail?: string }
            message = error.error ?? error.detail ?? message
        } catch { /* Keep status if a proxy returns a non-JSON error. */ }
        throw new Error(message)
    }
    return response.status === 204 ? undefined as T : response.json() as Promise<T>
}

/** Host-bound, published styles only. No persistent browser cache that can leak between tenants. */
export function PublishedStyleProvider({ children }: { children: ReactNode }) {
    const [settings, setSettings] = useState<StyleSettings>(defaultStyle)
    useEffect(() => {
        let active = true
        let request = 0
        const refresh = () => {
            const current = ++request
            void styleRequest<StyleView>('/api/style/published')
                .then(style => { if (active && current === request) setSettings(style.settings) })
                .catch(() => { /* Retain last known good definition, or bundled CRM default. */ })
        }
        refresh()
        const timer = window.setInterval(refresh, 60_000)
        window.addEventListener('marque-style-published', refresh)
        return () => { active = false; window.clearInterval(timer); window.removeEventListener('marque-style-published', refresh) }
    }, [])
    useEffect(() => {
        const root = document.documentElement
        root.dataset.marqueStyle = ''
        const vars = styleVariables(settings)
        for (const [key, value] of Object.entries(vars)) root.style.setProperty(key, String(value))
        return () => { for (const key of Object.keys(vars)) root.style.removeProperty(key) }
    }, [settings])
    return <StyleContext.Provider value={settings}>{children}</StyleContext.Provider>
}
